import React, { useState, useEffect, useContext } from 'react'
import { Toast } from 'primereact/toast'
import { Row, Col, Card, CardBody, CardTitle } from 'reactstrap'
import { InputText } from 'primereact/inputtext'
import { Button } from 'primereact/button'
import RecommendationsSearchResults from './RecommendationsSearchResults'
import PaNavigatorDisclaimer from './PaNavigatorDisclaimer'
import server from '../../server'
import { AuthenticationContext } from '../../Auth/authentication.context'
import useTableRowsPerPage from '../../hooks/useTableRowsPerPage'
import { getInitialPaginationState } from '../../shared/tablePagination'

const Recommendations = () => {
  const { user } = useContext(AuthenticationContext)
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage()
  const [searchVal, setSearchVal] = useState('')
  const [submittedQuery, setSubmittedQuery] = useState('')
  const [tableLoading, setTableLoading] = useState(false)
  const [products, setProducts] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [state, setState] = useState(() => getInitialPaginationState(user))

  useEffect(() => {
    setState((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])

  useEffect(() => {
    if (submittedQuery !== '') {
      handleFetchData(submittedQuery)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedQuery, state.page, state.rows])

  const handleFetchData = (query) => {
    if (query !== '') {
      setTableLoading(true)
      
      // Normalize search query: if it's all digits, strip leading zeros
      let normalizedQuery = query
      const trimmedSearch = query.trim()
      if (/^\d+$/.test(trimmedSearch)) {
        const parsed = parseInt(trimmedSearch, 10)
        normalizedQuery = isNaN(parsed) ? trimmedSearch : String(parsed)
      }
      
      server
        .get(`/products/`, {
          params: {
            q: normalizedQuery,
            page: state.page + 1,
            limit: state.rows,
          },
          headers: {
            // PA Navigator lists every manufacturer for guidance, including
            // ones this pharmacy cannot order on Origins Direct.
            'x-system-name': 'Origins Management',
          },
        })
        .then((response) => {
          setProducts(response.data.data)
          setTotalCount(response.data.totalCount || 0)
          setTableLoading(false)
        })
        .catch((error) => {
          setTableLoading(false)
          setTotalCount(0)
          console.error('Error fetching products:', error)
        })
    }
  }

  const handleSearch = () => {
    const query = searchVal.trim()
    if (!query) return
    setState((prev) => ({ ...prev, page: 0, first: 0 }))
    setSubmittedQuery(query)
  }

  const handleResultsUpdate = (updatedResults) => {
    setProducts(updatedResults)
  }

  const handleClearSearch = () => {
    setSearchVal('')
    setSubmittedQuery('')
    setProducts([])
    setTotalCount(0)
    setState({ first: 0, rows: defaultRows, page: 0 })
  }

  return (
    <React.Fragment>
      <div className='card flex justify-content-center'>
        <Toast />
      </div>
      <div className='page-content '>
        <div className='container-fluid'>
          <h3 className='fs-3 fw-bold mb-3'>PA Navigator</h3>
          <PaNavigatorDisclaimer className='mb-4' />
          <Row>
            <Col lg='12'>
              <Card>
                <CardBody>
                  <CardTitle tag='h5'>Search</CardTitle>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault()
                      handleSearch()
                    }}
                  >
                    <div className='flex justify-content-end w-100'>
                      <div className='d-flex gap-2'>
                        <span className='p-input-icon-left w-100 my-auto'>
                          <i className='pi pi-search' />
                          <InputText
                            placeholder='search by UPC, DIN, Name or Manufacturer'
                            className='w-100'
                            value={searchVal}
                            onChange={(e) => setSearchVal(e.target.value)}
                          />
                        </span>
                        <Button
                          type='submit'
                          label='Search'
                          style={{ minWidth: '100px' }}
                        />
                        <Button
                          type='button'
                          label='Clear'
                          severity='secondary'
                          outlined
                          style={{ minWidth: '100px' }}
                          onClick={handleClearSearch}
                        />
                      </div>
                    </div>
                  </form>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <h3 className='fs-3 fw-bold  mb-4'>Search Results</h3>
          <Row className='mb-3'>
            <Col lg='12'>
              <RecommendationsSearchResults
                results={products}
                lazyState={state}
                rowsPerPageOptions={rowsPerPageOptions}
                onLazyPage={(e) => {
                  setState((prev) => applyPageEvent(prev, e))
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
                totalCount={totalCount}
                tableLoading={tableLoading}
                handleResultsUpdate={handleResultsUpdate}
              />
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  )
}

export default Recommendations

